/**
 * Die Woerter der Buchhaltung — und die Regeln, die aus Feldern Farben machen.
 *
 * Bewusst ohne React: das hier ist reine Zuordnung und laesst sich deshalb
 * testen, ohne eine Seite zu rendern. Die Chips in `/eingang` und die Ampel in
 * der Kopfzeile haengen an genau diesen Funktionen.
 *
 * Die Sprache stammt aus `docs/business/buchhaltung/prozesse.md` — dasselbe
 * Wort in allen Apps schlaegt das je treffendste.
 */

import type {
  Belegart,
  Belegstatus,
  DatevStatus,
  Entscheidung,
  IbanStand,
  MatchStatus,
  Pruefschluessel,
  SteuerHinweis,
  VerwerfGrund,
  Warnstufe,
  Warnung,
} from '@/types';

export const ART_LABEL: Record<Belegart, string> = {
  rechnung: 'Rechnung',
  gutschrift: 'Gutschrift',
  mahnung: 'Mahnung',
  bwa: 'BWA',
  opos: 'Offene Posten',
  kontoauszug: 'Kontoauszug',
  sonstiges: 'Sonstiges',
  unklar: 'Unklar',
};

export const STATUS_LABEL: Record<Belegstatus, string> = {
  neu: 'Neu',
  pruefung: 'Prüfung',
  geprueft: 'Geprüft',
  freigegeben: 'Freigegeben',
  uebergeben: 'Übergeben',
  abgelehnt: 'Abgelehnt',
  duplikat: 'Dublette',
};

export const DATEV_STATUS_LABEL: Record<DatevStatus, string> = {
  offen: 'noch nicht übergeben',
  uebergeben: 'an die Kanzlei übergeben',
  uebergeben_probe: 'Probe (Trockenmodus)',
  fehlgeschlagen: 'Übergabe fehlgeschlagen',
};

export const STEUER_HINWEIS_LABEL: Record<SteuerHinweis, string> = {
  regel: 'Regelbesteuerung',
  reverse_charge: 'Reverse Charge (§ 13b UStG)',
  innergemeinschaftlich: 'innergemeinschaftliche Lieferung',
  kleinunternehmer: 'Kleinunternehmer (§ 19 UStG)',
  steuerfrei: 'steuerfrei',
  kleinbetrag: 'Kleinbetragsrechnung (§ 33 UStDV)',
};

export const IBAN_STAND_LABEL: Record<IbanStand, string> = {
  bestaetigt: 'bestätigt',
  unbestaetigt: 'IBAN unbestätigt',
  keine: 'keine IBAN hinterlegt',
};

/**
 * Die fuenf Gruende, aus denen ein Fund der Pruefliste kein Beleg wird.
 *
 * Dieselben fuenf wie im Ablehnen-Dialog des Belegs (`VerwerfGrund` im
 * Schema) — ein Wort fuer denselben Sachverhalt, egal an welcher Stelle.
 */
export const VERWERF_GRUND_LABEL: Record<VerwerfGrund, string> = {
  newsletter: 'Newsletter',
  angebot: 'Angebot',
  privat: 'privat',
  dublette: 'Dublette',
  sonstiges: 'sonstiges',
};

/** Die Entscheidung eines Funds, in Worten. */
export const ENTSCHEIDUNG_LABEL: Record<Entscheidung, string> = {
  offen: 'offen',
  uebernommen: 'übernommen',
  verworfen: 'verworfen',
};

/**
 * Die drei Quellen des Belegeingangs, wie sie ein Mensch nennt.
 *
 * `/health/ingest` liefert `quelle` als freien String — kaeme eine vierte
 * hinzu, faellt sie hier durch und wird unveraendert angezeigt, statt die
 * Kopfzeile zu leeren.
 */
export const QUELLE_LABEL: Record<string, string> = {
  sammelpostfach: 'Sammelpostfach',
  sweeper: 'Sweeper',
  ebics: 'Bank',
};

export function quelleLabel(quelle: string): string {
  return QUELLE_LABEL[quelle] ?? quelle;
}

export const MATCH_STATUS_LABEL: Record<MatchStatus, string> = {
  offen: 'offen',
  vorschlag: 'Vorschlag',
  zugeordnet: 'zugeordnet',
  ignoriert: 'ignoriert',
};

/**
 * Die Kurzform einer Warnung fuer den Chip.
 *
 * Der ausformulierte Satz steht im Feld `text` und kommt vom Server; das hier
 * ist die Beschriftung, die auf einen Chip passt. Beides zu haben ist kein
 * Widerspruch — die Liste hat drei Zentimeter, die Detailseite hat eine Zeile.
 */
export const WARNUNG_KURZ: Record<Pruefschluessel, string> = {
  aussteller_neu: 'Aussteller neu',
  aussteller_lookalike: 'Domain täuschend ähnlich',
  iban_geaendert: 'IBAN geändert',
  iban_unbestaetigt: 'IBAN unbestätigt',
  iban_nur_bild: 'IBAN nicht prüfbar',
  absender_fremd: 'Absender passt nicht',
  unerwartet_haeufig: 'ungewöhnlich häufig',
  betrag_ungewoehnlich: 'Betrag ungewöhnlich',
  pflichtangaben_fehlen: 'Pflichtangaben fehlen',
  empfaenger_falsch: 'Empfänger falsch',
  duplikat_nummer: 'Nummer doppelt',
  rechnung_fehlt: 'Rechnung fehlt',
  xml_weicht_ab: 'XML weicht ab',
  unvollstaendig: 'unvollständig',
};

/** Die Ampel: rot schlaegt gelb, gelb schlaegt gruen. */
export type Ampel = 'rot' | 'gelb' | 'gruen';

/**
 * Aus den Zaehlern der Liste eine Ampelfarbe machen.
 *
 * Rot gewinnt immer. Wer eine rote Warnung neben einer gelben gruen faerbte,
 * haette den Betrugsfall in der Zeile mit der harmlosen Meldung versteckt.
 */
export function ampelAusZaehlern(rot: number, gelb: number): Ampel {
  if (rot > 0) return 'rot';
  if (gelb > 0) return 'gelb';
  return 'gruen';
}

/** Dieselbe Entscheidung, aber aus den ausgeschriebenen Warnungen. */
export function ampelAusWarnungen(warnungen: Warnung[]): Ampel {
  return ampelAusZaehlern(
    warnungen.filter((w) => w.stufe === 'rot').length,
    warnungen.filter((w) => w.stufe === 'gelb').length,
  );
}

/** Die Tailwind-Klassen eines Chips. Eine Stelle, damit rot ueberall rot ist. */
export function ampelKlassen(ampel: Ampel): string {
  switch (ampel) {
    case 'rot':
      return 'bg-danger-50 text-danger-700 border-danger-200';
    case 'gelb':
      return 'bg-warning-50 text-warning-700 border-warning-200';
    case 'gruen':
      return 'bg-success-50 text-success-700 border-success-200';
  }
}

export function warnstufeKlassen(stufe: Warnstufe): string {
  return ampelKlassen(stufe === 'rot' ? 'rot' : 'gelb');
}

/**
 * Darf dieser Beleg noch bearbeitet werden?
 *
 * Bis `uebergeben` ist ein `PATCH` eine Korrektur. Danach ist es eine
 * `nachkorrektur` mit zweiter Upload-Mail an die Kanzlei (§15.2) — das baut
 * die API, nicht die Oberflaeche; hier werden die Felder schlicht gesperrt,
 * bis dieser Weg steht.
 */
export function istBearbeitbar(status: Belegstatus): boolean {
  return status !== 'uebergeben' && status !== 'abgelehnt' && status !== 'duplikat';
}

/**
 * Hat der Beleg eine rote Warnung?
 *
 * Rot heisst: nur `admin`, und nur mit Rueckrufnummer und Namen des
 * Gespraechspartners (§15.1 S3). Diese Oberflaeche gibt Rot deshalb **nicht**
 * frei — der Knopf ist da und sagt, was fehlt; den Dialog mit den
 * Pflichtfeldern baut Bahn H.
 */
export function hatRoteWarnung(warnungen: Warnung[]): boolean {
  return warnungen.some((w) => w.stufe === 'rot');
}

/**
 * Ist die IBAN dieses Belegs bestaetigt?
 *
 * **Abgeleitet, nicht gelesen.** `DocumentResponse` hat kein Feld
 * `iban_bestaetigt` — die Auskunft steckt in den Warnungen: solange eine der
 * drei IBAN-Pruefungen anschlaegt, ist sie es nicht. Verschwindet die Warnung
 * (weil ein Admin nach Rueckruf freigegeben hat), gilt sie als bestaetigt.
 *
 * Das ist ein Schluss und keine Tatsache. Steht das Feld eines Tages im
 * Vertrag, gehoert es hierher — die Aufrufer bleiben, wie sie sind.
 */
export function ibanStandAusWarnungen(
  iban: string | null | undefined,
  warnungen: Warnung[],
): IbanStand {
  if (!iban) return 'keine';
  const offen = warnungen.some(
    (w) =>
      w.schluessel === 'iban_geaendert' ||
      w.schluessel === 'iban_unbestaetigt' ||
      w.schluessel === 'iban_nur_bild',
  );
  return offen ? 'unbestaetigt' : 'bestaetigt';
}

/** Ist die Datei ein PDF, ein Bild oder eine E-Rechnung? */
export type Vorschauart = 'pdf' | 'bild' | 'xml' | 'unbekannt';

/**
 * Wie wird die Datei angezeigt?
 *
 * XML ist der Grund, warum es diese Funktion gibt: eine XRechnung hat kein
 * Bild. Sie im `iframe` zu zeigen hiesse, dem Menschen Rohtext vorzulegen —
 * gezeigt wird stattdessen die Feldtabelle aus den ausgelesenen Feldern
 * (Bauplan §15, S1).
 */
export function vorschauart(mimeType: string | null | undefined): Vorschauart {
  const typ = (mimeType || '').toLowerCase();
  if (typ.includes('pdf')) return 'pdf';
  if (typ.startsWith('image/')) return 'bild';
  if (typ.includes('xml')) return 'xml';
  return 'unbekannt';
}
